/**
 * AtmospherePersonaCategoryPage - 人设类目管理页面
 */

import { useState, useEffect, useCallback } from "react";
import {
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "../components/icons";
import { trpc } from "../trpc";

const { Title } = Typography;

interface AtmospherePersonaCategoryItem {
  categoryId: string;
  code: string;
  name: string;
  demographicDesc: string;
  personaTags: string[];
  avatarFeatures: string[];
  toneFeatures: string[];
  suitableScenes: string[];
  suggestedCount53: number;
  suggestedCount61: number;
  sortOrder: number;
  enabled: boolean;
}

export function AtmospherePersonaCategoryPage() {
  const { message } = App.useApp();

  const [categories, setCategories] = useState<AtmospherePersonaCategoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AtmospherePersonaCategoryItem | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [form] = Form.useForm();

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.atmospherePersonaCategory.list.query();
      setCategories(data);
    } catch {
      message.error("加载人设类目失败");
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const columns: ColumnsType<AtmospherePersonaCategoryItem> = [
    {
      title: "编号",
      dataIndex: "code",
      key: "code",
      width: 60,
    },
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      width: 140,
    },
    {
      title: "人群描述",
      dataIndex: "demographicDesc",
      key: "demographicDesc",
      ellipsis: true,
    },
    {
      title: "53人建议",
      dataIndex: "suggestedCount53",
      key: "suggestedCount53",
      width: 90,
    },
    {
      title: "61人建议",
      dataIndex: "suggestedCount61",
      key: "suggestedCount61",
      width: 90,
    },
    {
      title: "启用",
      dataIndex: "enabled",
      key: "enabled",
      width: 70,
      render: (enabled: boolean) => (enabled ? "是" : "否"),
    },
    {
      title: "操作",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="删除类目"
            description={`确定要删除 "${record.name}" 吗？`}
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleEdit = (record: AtmospherePersonaCategoryItem) => {
    setEditingCategory(record);
    form.setFieldsValue(record);
    setEditOpen(true);
  };

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      personaTags: [],
      avatarFeatures: [],
      toneFeatures: [],
      suitableScenes: [],
      suggestedCount53: 0,
      suggestedCount61: 0,
      sortOrder: categories.length + 1,
    });
    setEditOpen(true);
  };

  const handleSave = async (values: AtmospherePersonaCategoryItem) => {
    setSaveLoading(true);
    try {
      await trpc.atmospherePersonaCategory.upsert.mutate(values);
      message.success("类目保存成功");
      setEditOpen(false);
      form.resetFields();
      setEditingCategory(null);
      loadCategories();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "类目保存失败");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (record: AtmospherePersonaCategoryItem) => {
    try {
      await trpc.atmospherePersonaCategory.delete.mutate({ categoryId: record.categoryId });
      message.success("删除成功");
      loadCategories();
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "删除失败");
    }
  };

  return (
    <div className="fade-in">
      <Card
        variant="borderless"
        className="shadow-sm"
        title={
          <Title level={5} style={{ margin: 0 }}>
            人设类目管理
          </Title>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadCategories} loading={loading}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新增类目
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={categories}
          rowKey="categoryId"
          loading={loading}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Spin spinning={loading}>
                <div style={{ padding: "40px 0" }}>暂无数据</div>
              </Spin>
            ),
          }}
        />
      </Card>

      <Modal
        title={editingCategory ? "编辑人设类目" : "新增人设类目"}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          form.resetFields();
          setEditingCategory(null);
        }}
        footer={null}
        destroyOnHidden
        width={720}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="categoryId"
            label="类目 ID"
            rules={[{ required: true, message: "请输入类目 ID" }]}
          >
            <Input placeholder="如 cat_grandma" disabled={!!editingCategory} />
          </Form.Item>

          <Space style={{ width: "100%" }} align="start">
            <Form.Item
              name="code"
              label="编号"
              rules={[{ required: true, message: "请输入编号" }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="如 1" />
            </Form.Item>
            <Form.Item
              name="name"
              label="名称"
              rules={[{ required: true, message: "请输入名称" }]}
              style={{ flex: 2 }}
            >
              <Input placeholder="如 奶奶辈" />
            </Form.Item>
            <Form.Item name="sortOrder" label="排序" style={{ width: 100 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item name="demographicDesc" label="人群描述">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="personaTags" label="人设标签">
            <Select mode="tags" placeholder="输入后回车添加" />
          </Form.Item>

          <Form.Item name="avatarFeatures" label="头像特征">
            <Select mode="tags" placeholder="输入后回车添加" />
          </Form.Item>

          <Form.Item name="toneFeatures" label="语气特征">
            <Select mode="tags" placeholder="输入后回车添加" />
          </Form.Item>

          <Form.Item name="suitableScenes" label="适用场景">
            <Select mode="tags" placeholder="输入后回车添加" />
          </Form.Item>

          <Space style={{ width: "100%" }} align="start">
            <Form.Item name="suggestedCount53" label="53人建议数量">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item name="suggestedCount61" label="61人建议数量">
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item name="enabled" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setEditOpen(false);
                  form.resetFields();
                  setEditingCategory(null);
                }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
